import { useState, useCallback, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Clock as ClockIcon, Plus as PlusIcon } from 'lucide-react'
import { useSubscription } from '../context/SubscriptionContext'
import { api, type ScheduleResponse, type ScheduleBlock } from '../lib/api'
import { queries } from '../lib/queries'
import WeeklySchedule from './WeeklySchedule'
import ScheduleModal from './ScheduleModal'

const ToggleIcon = ({ enabled }: { enabled: boolean }) => (
  <div className={`w-10 h-6 rounded-full relative transition-colors ${enabled ? 'bg-brand' : 'bg-gray-300'}`}>
    <div className={`absolute w-4 h-4 rounded-full bg-white top-1 transition-transform shadow-sm ${enabled ? 'translate-x-5' : 'translate-x-1'}`} />
  </div>
)

export interface SchedulesClientProps {
  showNewModal?: boolean
}

export default function SchedulesClient({
  showNewModal = false,
}: SchedulesClientProps) {
  const queryClient = useQueryClient()
  const { canPerformPaidActions, isLoading: subscriptionLoading } = useSubscription()
  const showSubscriptionRestriction = !subscriptionLoading && !canPerformPaidActions
  const [error, setError] = useState<string | null>(null)

  // Modal state
  const [modalOpen, setModalOpen] = useState(false)
  const [editingSchedule, setEditingSchedule] = useState<ScheduleResponse | null>(null)
  const [modalInitialDay, setModalInitialDay] = useState<number | undefined>(undefined)
  const [modalInitialStart, setModalInitialStart] = useState<string | undefined>(undefined)
  const [modalInitialEnd, setModalInitialEnd] = useState<string | undefined>(undefined)

  const { data: schedulesData, isLoading: schedulesLoading } = useQuery({
    queryKey: queries.schedules.list(),
    queryFn: () => api.getSchedules(),
  })
  const { data: policiesData } = useQuery({
    queryKey: queries.policies.list(),
    queryFn: () => api.getPolicies(),
  })
  const { data: devicesData } = useQuery({
    queryKey: queries.devices.list(),
    queryFn: () => api.getDevices(),
  })

  const schedules = schedulesData?.schedules ?? []
  const policies = policiesData?.policies ?? []
  const devices = devicesData?.devices ?? []
  const loading = schedulesLoading

  // Open new modal on ?new=true
  useEffect(() => {
    if (showNewModal && devices.length > 0 && policies.length > 0) {
      setEditingSchedule(null)
      setModalInitialDay(undefined)
      setModalInitialStart(undefined)
      setModalInitialEnd(undefined)
      setModalOpen(true)
    }
  }, [showNewModal, devices.length, policies.length])

  const invalidateSchedules = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queries.schedules.list() })
  }, [queryClient])

  const handleToggleSchedule = useCallback(
    async (scheduleId: string) => {
      try {
        await api.toggleSchedule(scheduleId)
        invalidateSchedules()
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to toggle schedule')
      }
    },
    [invalidateSchedules],
  )

  const handleNewSchedule = () => {
    setEditingSchedule(null)
    setModalInitialDay(undefined)
    setModalInitialStart(undefined)
    setModalInitialEnd(undefined)
    setModalOpen(true)
  }

  const handleCreateBlock = (dayOfWeek: number, startTime: string, endTime: string) => {
    setEditingSchedule(null)
    setModalInitialDay(dayOfWeek)
    setModalInitialStart(startTime)
    setModalInitialEnd(endTime)
    setModalOpen(true)
  }

  const handleClickBlock = (schedule: ScheduleResponse, _block: ScheduleBlock) => {
    setEditingSchedule(schedule)
    setModalInitialDay(undefined)
    setModalInitialStart(undefined)
    setModalInitialEnd(undefined)
    setModalOpen(true)
  }

  const handleModalClose = () => {
    setModalOpen(false)
    setEditingSchedule(null)
  }

  const handleSave = async (data: {
    name: string
    policyId: string
    timezone: string
    blocks: Array<{ dayOfWeek: number; startTime: string; endTime: string }>
    deviceIds: string[]
  }) => {
    let result: ScheduleResponse
    if (editingSchedule) {
      result = await api.updateSchedule(editingSchedule.id, data)
    } else {
      result = await api.createSchedule(data)
    }
    invalidateSchedules()
    if (result.sync?.status === 'partial') {
      const failedCount = result.sync.failedDeviceIds.length
      const deviceLabel = failedCount === 1 ? '1 device' : failedCount > 1 ? `${failedCount} devices` : 'some devices'
      setError(`Schedule saved, but it could not be pushed to ${deviceLabel}. Enforcement will retry automatically.`)
    }
  }

  const handleDelete = async () => {
    if (!editingSchedule) return
    await api.deleteSchedule(editingSchedule.id)
    invalidateSchedules()
  }

  const defaultTimezone = schedules[0]?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York'

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
      <div className="flex items-start sm:items-center justify-between gap-4 mb-4 sm:mb-6">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Schedules</h1>
          <p className="text-sm text-gray-500 mt-1">Create time-based rules that automatically apply to devices</p>
        </div>
        {devices.length > 0 && policies.length > 0 && (
          <div className="flex flex-col items-end gap-1">
            <button
              onClick={handleNewSchedule}
              disabled={showSubscriptionRestriction}
              className="inline-flex items-center gap-2 px-4 py-2.5 text-sm bg-brand text-white rounded-lg hover:bg-brand-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title={showSubscriptionRestriction ? 'Active subscription required' : undefined}
            >
              <PlusIcon size={20} />
              <span>New Schedule</span>
            </button>
            {showSubscriptionRestriction && <p className="text-xs text-amber-600">Reactivate subscription to create</p>}
          </div>
        )}
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline text-red-700">Dismiss</button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center min-h-[300px] sm:min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand" />
        </div>
      ) : (
        <>
          {devices.length === 0 || policies.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-100 p-6 sm:p-8 text-center">
              <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
                <ClockIcon size={24} className="text-gray-500" />
              </div>
              <h3 className="text-base font-semibold text-gray-900 mb-1">Setup Required</h3>
              <p className="text-gray-500 text-sm mb-4">
                {devices.length === 0 && policies.length === 0
                  ? 'Add a device and create a rule first.'
                  : devices.length === 0
                    ? 'Add a device first.'
                    : 'Create a rule first.'}
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
                {devices.length === 0 && (
                  <Link to="/devices" className="inline-flex items-center justify-center px-4 py-2.5 text-sm bg-brand text-white rounded-lg hover:bg-brand-hover transition-colors">
                    Add Device
                  </Link>
                )}
                {policies.length === 0 && (
                  <Link to="/rules" className="inline-flex items-center justify-center px-4 py-2.5 text-sm bg-brand text-white rounded-lg hover:bg-brand-hover transition-colors">
                    Create Rule
                  </Link>
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <WeeklySchedule
                  schedules={schedules}
                  timezone={defaultTimezone}
                  onCreateBlock={handleCreateBlock}
                  onClickBlock={handleClickBlock}
                />
              </div>

              {schedules.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <h2 className="text-sm font-semibold text-gray-700">All Schedules</h2>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {schedules.map((schedule) => (
                      <div
                        key={schedule.id}
                        className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer"
                        onClick={() => handleClickBlock(schedule, schedule.blocks[0])}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-800 truncate">{schedule.name}</div>
                          <div className="text-xs sm:text-sm text-gray-500 truncate">
                            <span>{schedule.policy.name}</span>
                            <span className="mx-1.5">·</span>
                            <span>{schedule.devices.length} device{schedule.devices.length !== 1 ? 's' : ''}</span>
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            if (!schedule.isEnabled && showSubscriptionRestriction) return
                            handleToggleSchedule(schedule.id)
                          }}
                          role="switch"
                          aria-checked={schedule.isEnabled}
                          aria-label={`Toggle ${schedule.name}`}
                          className={`shrink-0 ${!schedule.isEnabled && showSubscriptionRestriction ? 'opacity-50 cursor-not-allowed' : ''}`}
                          title={schedule.isEnabled ? 'Disable schedule' : 'Enable schedule'}
                        >
                          <ToggleIcon enabled={schedule.isEnabled} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {schedules.length === 0 && (
                <div className="bg-white rounded-xl border border-gray-100 p-6 sm:p-8 text-center">
                  <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
                    <ClockIcon size={24} className="text-gray-500" />
                  </div>
                  <h3 className="text-base font-semibold text-gray-900 mb-1">No schedules yet</h3>
                  <p className="text-gray-500 text-sm">Create your first schedule to manage device access throughout the day</p>
                </div>
              )}
            </>
          )}
        </>
      )}

      <ScheduleModal
        variant="family"
        isOpen={modalOpen}
        onClose={handleModalClose}
        onSave={handleSave}
        onDelete={editingSchedule ? handleDelete : undefined}
        schedule={editingSchedule}
        policies={policies}
        devices={devices}
        initialDayOfWeek={modalInitialDay}
        initialStartTime={modalInitialStart}
        initialEndTime={modalInitialEnd}
      />
    </div>
  )
}
